import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Check, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import GoldMarquee from "@/components/Marquee";
import Calculator from "@/components/Calculator";
import Trust from "@/components/Trust";
import ProjectsMap from "@/components/ProjectsMap";
import Gallery from "@/components/Gallery";
import Footer from "@/components/Footer";
import SdPromo from "@/components/SdPromo";
import FirmCards from "@/components/FirmCards";
import StickyCTA from "@/components/StickyCTA";
import { api, scrollToId, SITE_URL, imgUrl, IS_BOT } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1657346088167-b982455bf29a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setMetaProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const PRICING_ROWS = [
  { m: "EPS 80 mm", price: "58 – 72 €/m²" },
  { m: "EPS 120 mm", price: "66 – 84 €/m²" },
  { m: "EPS 160 mm", price: "74 – 96 €/m²" },
  { m: "Minerálna vata 140 mm", price: "82 – 108 €/m²" },
];

// Per-town variant paragraphs — deterministic by slug hash so 1 000 landings are not byte-identical.
const VARIANTS = [
  (t, okresCount) => `Okres ${t.okres} patrí medzi lokality, kde riešime zateplenie pravidelne — od menších rodinných domov po bytové objekty. V rámci okresu pokrývame ${okresCount} obcí, takže obhliadku aj realizáciu vieme naplánovať rýchlo a bez príplatkov za dlhú dopravu.`,
  (t, okresCount) => `Majitelia domov v obci ${t.name} najčastejšie riešia staršie nezateplené fasády a vysoké účty za kúrenie. Spolu s ďalšími ${okresCount} obcami v okrese ${t.okres} máme v regióne ${t.region} dostupných overených realizátorov, ktorí poznajú miestne typy stavieb.`,
  (t, okresCount) => `Pri zatepľovaní v ${t.name} vždy posudzujeme stav muriva, vlhkosť a prístupnosť lešenia — každá fasáda je iná. V okrese ${t.okres} (${okresCount} obcí) zabezpečujeme kompletnú realizáciu na kľúč s písomnou cenovou ponukou pred začatím prác.`,
];

const FAQS = (name) => [
  { q: `Koľko stojí zateplenie v meste ${name}?`, a: `Cena zateplenia v meste ${name} závisí od plochy fasády, hrúbky a materiálu — orientačne 60–110 €/m² vrátane materiálu, práce, lešenia a dopravy. Presný prepočet pre váš dom v meste ${name} získate v kalkulačke do 60 sekúnd.` },
  { q: `Ako dlho trvá zateplenie rodinného domu v meste ${name}?`, a: "Bežne 2 až 4 týždne podľa počasia a rozsahu prác." },
  { q: "Akú hrúbku zateplenia zvoliť?", a: "Najčastejšie 140–200 mm. Optimálnu hrúbku vám odporučíme podľa typu budovy." },
  { q: `Kto zrealizuje zateplenie v okolí mesta ${name}?`, a: `V regióne máme overených realizačných partnerov s referenciami. Po výpočte ceny vás spojíme s partnerom, ktorý pôsobí v okolí mesta ${name}.` },
  { q: "Akú záruku poskytujete?", a: "36 mesiacov na prácu a 60 mesiacov na materiál." },
];

export default function SeoLandingPage() {
  const { kw, town } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [keywords, setKeywords] = useState([]);
  const [nearby, setNearby] = useState([]);
  const [nearProjects, setNearProjects] = useState([]);
  const [localFirms, setLocalFirms] = useState([]);

  useEffect(() => {
    setState("loading");
    api.get(`/seo/resolve/${kw}/${town}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [kw, town]);

  useEffect(() => {
    if (state !== "ok" || !data) return;
    api.get("/seo/keywords").then((r) => setKeywords(r.data)).catch(() => {});
    api.get(`/seo/nearby/${data.town.slug}`).then((r) => setNearProjects(r.data || [])).catch(() => {});
    if (data.town.okres) {
      api.get(`/firms/public?district=${encodeURIComponent(data.town.okres)}`).then((r) => setLocalFirms(r.data || [])).catch(() => {});
    }
    api.get("/seo/towns").then((r) => {
      const same = r.data.filter((t) => t.region === data.town.region && t.slug !== data.town.slug).slice(0, 12);
      setNearby(same.length ? same : r.data.filter((t) => t.slug !== data.town.slug).slice(0, 12));
    }).catch(() => {});

    document.title = data.title;
    setMeta("description", data.intro);
    const t = data.town;
    const canonical = `${SITE_URL}/${kw}/${t.slug}`;
    setCanonical(canonical);
    setMetaProp("og:title", data.title);
    setMetaProp("og:description", data.intro);
    setMetaProp("og:type", "website");
    setMetaProp("og:url", canonical);
    setMetaProp("og:image", HERO_IMG);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", data.title);
    setMeta("twitter:description", data.intro);
    setMeta("twitter:image", HERO_IMG);
    const okresOk = t.okres && t.okres !== "undefined";
    const faq = FAQS(t.name);
    const ld = {
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "LocalBusiness", "@id": `${canonical}#business`, "name": `Zateplenia.eu – ${t.name}`, "description": data.intro,
          "areaServed": t.name, "address": { "@type": "PostalAddress", "addressLocality": t.name, "addressRegion": t.region, "addressCountry": "SK" },
          ...(t.lat && t.lng ? { "geo": { "@type": "GeoCoordinates", "latitude": t.lat, "longitude": t.lng } } : {}),
          "priceRange": "€€", "url": canonical, "email": "info@zateplenia.eu",
          "image": `${SITE_URL}/og-image.jpg`, "inLanguage": "sk-SK" },
        { "@type": "Service", "serviceType": data.h1, "provider": { "@type": "Organization", "name": "Zateplenia.eu", "url": SITE_URL },
          "areaServed": { "@type": "City", "name": t.name } },
        { "@type": "FAQPage", "mainEntity": faq.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) },
        { "@type": "BreadcrumbList", "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Domov", "item": SITE_URL + "/" },
          { "@type": "ListItem", "position": 2, "name": okresOk ? `Okres ${t.okres}` : t.region, "item": canonical },
          { "@type": "ListItem", "position": 3, "name": data.h1, "item": canonical },
        ] },
      ],
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, [state, data, kw]);

  if (state === "loading") {
    return <div className="min-h-screen bg-ink flex items-center justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="min-h-screen bg-ink text-white flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-5xl">Stránka nenájdená</h1>
        <Link to="/" className="text-gold underline">Späť na hlavnú stránku</Link>
      </div>
    );
  }

  const t = data.town;

  return (
    <div>
      <Header />

      <section className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMG} alt={data.h1} className="w-full h-full object-cover grayscale-[30%]" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        </div>
        <div className="relative z-10 max-w-[1400px] w-full mx-auto px-5 md:px-10 pb-16 pt-32">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-5">
              <MapPin className="w-4 h-4" /> {t.okres} · {t.region}
            </div>
            <h1 data-testid="seo-h1" className="font-display text-white leading-[0.9] text-4xl sm:text-6xl lg:text-8xl">
              {data.h1}
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6 leading-relaxed">{data.intro}</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button onClick={() => scrollToId("#kalkulacka")} data-testid="seo-cta"
                className="group bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider flex items-center gap-3 hover:bg-gold-light transition-colors gold-glow">
                Vypočítať cenu <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <div className="flex items-center gap-2 text-white/70 text-sm px-4 py-4 border border-white/15">
                <ShieldCheck className="w-5 h-5 text-gold" /> Záruka 36 / 60 mesiacov
              </div>
            </div>
            {data.has_partner && (
              <div className="mt-5 inline-flex items-center gap-2 text-gold text-sm border border-gold/40 bg-gold/5 px-4 py-2" data-testid="seo-partner">
                <ShieldCheck className="w-4 h-4" /> Overený partner v okrese {t.okres}: {data.partner_name}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <GoldMarquee />
      <Calculator presetDistrict={t.okres} />
      <Trust />
      <ProjectsMap />
      <Gallery />

      {/* Local content + pricing (unique per town) */}
      <section className="bg-ink py-24 border-t border-white/10" data-testid="seo-local-content">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <nav className="flex items-center gap-2 text-white/40 text-xs font-mono2 uppercase tracking-widest mb-8" data-testid="seo-breadcrumb">
            <Link to="/" className="hover:text-gold">Domov</Link><span>/</span>
            {t.okres && t.okres !== "undefined"
              ? <Link to={`/okres/${data.okres_slug}`} className="hover:text-gold">{`Okres ${t.okres}`}</Link>
              : <span>{t.region}</span>}<span>/</span>
            <span className="text-gold">{t.name}</span>
          </nav>
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Zateplenie v meste {t.name}</p>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6">Ceny a realizácia zateplenia v {t.name}</h2>
          <p className="text-white/60 leading-relaxed text-lg">
            {t.name}{t.population ? ` (${t.population.toLocaleString("sk-SK")} obyvateľov)` : ""} sa nachádza
            {t.okres && t.okres !== "undefined" ? ` v okrese ${t.okres}` : ""}, región {t.region}.
            Pre majiteľov rodinných aj bytových domov v meste {t.name} zabezpečujeme kompletné zateplenie fasády
            vrátane materiálu, práce, lešenia a dopravy — s orientačným prepočtom ceny online do 60 sekúnd
            a zárukou 36 mesiacov na prácu a 60 mesiacov na materiál.
          </p>
          {t.local_text && (
            <p className="text-white/60 leading-relaxed text-lg mt-5" data-testid="seo-local-text">{t.local_text}</p>
          )}
          {t.local_tip && (
            <div className="mt-6 border border-gold/30 bg-gold/5 px-5 py-4 flex items-start gap-3" data-testid="seo-local-tip">
              <Check className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <p className="text-white/80 text-sm leading-relaxed">
                <span className="text-gold font-semibold uppercase tracking-wider text-xs font-mono2 block mb-1">Tip pre {t.name}</span>
                {t.local_tip}
              </p>
            </div>
          )}

          {/* Variant paragraph — deterministic per town so landings are not byte-identical */}
          <p className="text-white/55 leading-relaxed mt-6" data-testid="seo-variant-text">
            {VARIANTS[[...t.slug].reduce((s, c) => s + c.charCodeAt(0), 0) % VARIANTS.length](t, data.okres_count)}
          </p>

          {/* Town map (OpenStreetMap embed — no key needed; skipped for crawler bots) */}
          {!IS_BOT && t.lat && t.lng && (
            <div className="mt-8 border border-white/10" data-testid="seo-town-map">
              <iframe title={`Mapa – ${t.name}`} loading="lazy" className="w-full h-[300px] grayscale-[35%]"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${t.lng - 0.08},${t.lat - 0.05},${t.lng + 0.08},${t.lat + 0.05}&layer=mapnik&marker=${t.lat},${t.lng}`} />
              <div className="px-4 py-2 text-white/30 text-xs border-t border-white/10">Mapa: © OpenStreetMap prispievatelia</div>
            </div>
          )}

          {/* Nearby towns in the same district — internal linking + unique content */}
          {data.nearby && data.nearby.length > 0 && (
            <div className="mt-8" data-testid="seo-nearby-towns">
              <div className="font-mono2 text-xs uppercase tracking-widest text-white/40 mb-3">
                Okolité obce v okrese {t.okres} ({data.okres_count} obcí)
              </div>
              <div className="flex flex-wrap gap-2">
                {data.nearby.map((n) => (
                  <Link key={n.slug} to={`/${data.keyword.slug}/${n.slug}`} data-testid={`nearby-${n.slug}`}
                    className="border border-white/10 px-3 py-1.5 text-sm text-white/60 hover:border-gold hover:text-gold transition-colors">{n.name}</Link>
                ))}
                <Link to={`/okres/${data.okres_slug}`} data-testid="nearby-okres"
                  className="border border-gold/40 px-3 py-1.5 text-sm text-gold hover:bg-gold/10 transition-colors">Celý okres {t.okres} →</Link>
              </div>
            </div>
          )}

          <div className="mt-10 border border-white/10">
            <div className="px-5 py-3 border-b border-white/10 font-mono2 text-xs uppercase tracking-widest text-gold">
              Orientačný cenník zateplenia — {t.name}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-white/40 text-xs uppercase tracking-wider">
                <th className="text-left px-5 py-3 font-normal">Skladba / hrúbka</th>
                <th className="text-right px-5 py-3 font-normal">Cena vrátane prác</th>
              </tr></thead>
              <tbody>
                {PRICING_ROWS.map((r) => (
                  <tr key={r.m} className="border-t border-white/5">
                    <td className="px-5 py-3 text-white/80">{r.m}</td>
                    <td className="px-5 py-3 text-right font-mono2 text-gold">{r.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 text-white/40 text-xs border-t border-white/10">
              Ceny sú orientačné pre {t.name} a okolie. Presný prepočet získate v kalkulačke vyššie.
            </div>
          </div>
        </div>
      </section>

      {/* Nearby real projects from the map */}
      {nearProjects.length > 0 && (
        <section className="bg-ink-2 py-24 border-t border-white/10" data-testid="seo-nearby-projects">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10">
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Realizácie v okolí</p>
            <h2 className="font-display text-4xl md:text-6xl text-white mb-4">Naše realizácie najbližšie k <span className="text-gold">{t.name}</span></h2>
            <p className="text-white/50 max-w-2xl mb-12">Skutočné zateplené fasády z nášho okolia. Kvalita, ktorú si viete pozrieť aj naživo.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {nearProjects.map((p) => (
                <div key={p.id} className="border border-white/10 bg-ink overflow-hidden group" data-testid={`nearby-project-${p.id}`}>
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={imgUrl(p.image)} alt={`${p.title} ${p.location}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-white font-semibold">{p.location}</h3>
                      <span className="text-gold text-xs font-mono2 flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.distance_km} km</span>
                    </div>
                    <p className="text-white/50 text-sm mt-1 line-clamp-2">{p.description || p.material}</p>
                    <div className="text-white/40 text-xs mt-2 font-mono2">{p.area ? `${p.area} m² · ` : ""}{p.year}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="bg-ink py-24 border-t border-white/10">
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Časté otázky</p>
          <h2 className="font-display text-4xl md:text-6xl text-white mb-12">{data.h1} — FAQ</h2>
          <div className="divide-y divide-white/10">
            {FAQS(t.name).map((f) => (
              <div key={f.q} className="py-6">
                <h3 className="text-white font-semibold text-lg mb-2">{f.q}</h3>
                <p className="text-white/60 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          {/* Related guides (FAQ↔blog interlinking) */}
          <div className="mt-10 pt-8 border-t border-white/10">
            <p className="font-mono2 text-gold uppercase tracking-widest text-xs mb-4">Užitočné články a návody</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/blog/kolko-stoji-zateplenie-2026" className="px-4 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors text-sm font-semibold">Koľko stojí zateplenie v roku 2026 (ceny)</Link>
              <Link to="/materialy" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Aký materiál zvoliť (EPS/vata)</Link>
              <Link to="/blog/postup-realizacie-zateplenia" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Postup realizácie krok po kroku</Link>
              <Link to="/blog/5-rad-pri-zateplovani" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">5 rád pri zatepľovaní</Link>
              <Link to="/faq" className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">Všetky časté otázky</Link>
            </div>
          </div>
        </div>
      </section>

      {/* internal links */}
      <section className="bg-ink-2 py-20 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 space-y-12">
          {keywords.length > 1 && (
            <div>
              <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Súvisiace služby v meste {t.name}</p>
              <div className="flex flex-wrap gap-3">
                {keywords.filter((k) => k.slug !== kw).map((k) => (
                  <Link key={k.slug} to={`/${k.slug}/${t.slug}`} data-testid={`seo-variant-${k.slug}`}
                    className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">
                    {k.h1_template.replace("{town}", t.name)}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {nearby.length > 0 && (
            <div>
              <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Zateplenie v okolí ({t.region})</p>
              <div className="flex flex-wrap gap-3">
                {nearby.map((n) => (
                  <Link key={n.slug} to={`/zateplenie/${n.slug}`}
                    className="px-4 py-2 border border-white/15 text-white/70 hover:border-gold hover:text-gold transition-colors text-sm">
                    Zateplenie {n.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {localFirms.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 md:px-8 py-14 border-t border-white/10" data-testid="seo-local-firms">
          <div className="flex items-center gap-2 text-gold text-xs font-mono2 uppercase tracking-[0.3em] mb-3">
            <ShieldCheck className="w-4 h-4" /> Overení realizátori
          </div>
          <h2 className="font-display text-3xl md:text-4xl mb-2">Realizačné firmy v okrese {t.okres}</h2>
          <p className="text-white/50 text-sm mb-8 max-w-2xl">Preverené firmy na zateplenie a fasády vo vašom okrese. Pozrite si ich referencie alebo si nechajte spočítať cenu vyššie — ozvú sa vám.</p>
          <FirmCards firms={localFirms} />
          <div className="mt-6">
            <Link to="/realizatori" className="text-gold text-sm font-bold uppercase tracking-widest inline-flex items-center gap-1 hover:underline">
              Celý katalóg realizátorov <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      <SdPromo context="seo" />
      <Footer />
      <StickyCTA context={`seo-${kw}-${t.slug}`} />
    </div>
  );
}
